import { EventType } from '../data-models/event-type.model';
import { MembershipRoles } from '../data-models/membership-roles.model';
import { OrganizationActivity } from '../data-models/organization-activity.model';
import { ResourceType } from '../data-models/resource-type.model';

/**
 * Role Bitwise Constants
 * Used for visibility_roles bitwise operations
 */
export const ROLE_OWNER = 1;

export const ROLE_CHIEF_SUPERVISOR = 2;

export const ROLE_PIC = 4;

export const ROLE_PILOT = 8;

/**
 * Convenience constant for all roles
 */
export const ALL_ROLES = ROLE_OWNER | ROLE_CHIEF_SUPERVISOR | ROLE_PIC | ROLE_PILOT; // 15

/**
 * Map event type to icon class
 */
export function getActivityIcon(eventType: EventType): string {
  switch (eventType) {
    case 'flight.created':
    case 'flight.started':
    case 'flight.completed':
      return 'icon-drone';
    case 'drone.created':
    case 'drone.status_changed':
    case 'drone.assigned':
      return 'icon-drone';
    case 'team.member_joined':
      return 'bi bi-person-plus';
    case 'customer.created':
      return 'bi bi-person';
    case 'field.created':
      return 'bi bi-geo-alt';
    case 'custom_chemical.created':
      return 'bi bi-droplet';
    case 'chemical_template.created':
      return 'bi bi-file-earmark-plus';
    case 'report.generated':
      return 'bi bi-file-earmark-text';
    case 'safety_test.completed':
    case 'safety_test.due':
      return 'bi bi-clipboard-check';
    case 'ticket.created':
      return 'bi bi-ticket-detailed';
    case 'ticket.closed':
      return 'bi bi-check-circle';
    case 'ticket.reopened':
      return 'bi bi-arrow-counterclockwise';
    case 'ticket.reply':
      return 'bi bi-chat-left-text';
    case 'appointment.requested':
      return 'bi bi-calendar-plus';
    case 'appointment.scheduled':
      return 'bi bi-calendar-check';
    case 'appointment.cancelled':
      return 'bi bi-calendar-x';
    case 'appointment.completed':
      return 'bi bi-calendar2-check';
    case 'work_order.created':
    case 'work_order.in_progress':
    case 'work_order.completed':
    case 'work_order.cancelled':
      return 'bi bi-wrench';
    case 'certification.uploaded':
    case 'certification.renewed':
      return 'bi bi-file-earmark-check';
    default:
      return 'bi bi-activity';
  }
}

/**
 * Map resource type to route path
 * Routes must match app-routing.module.ts definitions
 */
export function getResourceRoute(resourceType: ResourceType | null, resourceId: string | null): string | null {
  if (!resourceType || !resourceId) return null;

  switch (resourceType) {
    case 'spray_record':
      return `/logbook/spray-records/${resourceId}/view`;
    case 'drone':
      return `/drones/${resourceId}/view`;
    case 'customer':
      return `/customers/${resourceId}/view`;
    case 'field':
      // Fields have no view page; the edit page shows the field
      return `/logbook/fields/${resourceId}/edit`;
    case 'custom_chemical':
      return `/logbook/chemicals/custom/${resourceId}/view`;
    case 'chemical_template':
      return `/logbook/chemicals/templates/${resourceId}/view`;
    case 'faa_report':
      // FAA reports don't have individual view pages, go to list
      return `/logbook/reports/faa`;
    case 'state_report':
      // State reports don't have individual view pages, go to list
      return `/logbook/reports/state`;
    case 'field_test_submission':
      return `/logbook/testing/field/${resourceId}`;
    case 'support_ticket':
      return `/maintenance/support-tickets/${resourceId}`;
    case 'service_appointment':
      return `/maintenance/service-appointments/${resourceId}`;
    case 'work_order':
      return `/work-orders/${resourceId}`;
    case 'certification':
      return `/certifications`;
    case 'profile':
      // Team/profile page not yet implemented
      return null;
    default:
      return null;
  }
}

/**
 * Check if user role has visibility for an activity
 */
export function isActivityVisibleToRole(activity: OrganizationActivity, userRole: number): boolean {
  return (activity.visibilityRoles & userRole) !== 0;
}

/**
 * Relative time for an activity's timestamp: "Just now", "5 minutes ago", … then the date after a week.
 * A missing timestamp reads as an empty string.
 */
export function getActivityRelativeTime(createdAt: Date | null, now: Date = new Date()): string {
  if (!createdAt) return '';
  const diffSec = Math.floor((now.getTime() - createdAt.getTime()) / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'Just now';
  if (diffMin < 60) return `${diffMin} minute${diffMin !== 1 ? 's' : ''} ago`;
  if (diffHour < 24) return `${diffHour} hour${diffHour !== 1 ? 's' : ''} ago`;
  if (diffDay < 7) return `${diffDay} day${diffDay !== 1 ? 's' : ''} ago`;
  return createdAt.toLocaleDateString();
}

/** Whether an activity happened after the user last viewed the feed (`null` = never viewed: everything is unseen). */
export function isActivityUnseen(activity: OrganizationActivity, lastViewedAt: string | null): boolean {
  if (!lastViewedAt) return true;
  return activity.createdAt !== null && activity.createdAt > new Date(lastViewedAt);
}

/**
 * The activity visibility role for a user's membership claims in an organization: Owners and Chief
 * Supervisors see everything (`ROLE_OWNER`); everyone else, including a user without claims, `ROLE_PILOT`.
 */
export function activityRoleForClaims(claims: number | null | undefined): number {
  const seesEverything = MembershipRoles.Owner | MembershipRoles.ChiefSupervisor;
  return claims && (claims & seesEverything) !== 0 ? ROLE_OWNER : ROLE_PILOT;
}
